import type { OpenCv } from "@brickme/opencv";
import type { Bitmap, WritableBitmap } from "../bitmap/types.ts";
import {
	overlayBlend,
	vividLightBlend,
	bitmapChannels,
	cloneBitmapToWritable,
	createBlankWritableBitmap,
} from "../bitmap/index.ts";
import bitwiseNot from "../bitmap/bitwise-not.ts";
import { readUInt32BE, writeUInt32BE } from "../utils/uint8-array.ts";
import { floatFloor } from "../utils/float.ts";
import compileMaskingProps from "./compile-masking-props.ts";
import type { BuildOperationInput, BuildOperationResult } from "./operation.ts";
import { bitmapToOpenCv, cloneOpenCvToBitmap } from "../bitmap/opencv.ts";
import { isLegacyDetailFilter } from "../model/detail-filter.ts";

function* detailFilterAlgorithm(
	openCv: OpenCv,
	image: Bitmap,
	amplitude: number,
	sigmaA: number,
	sigmaB: number,
	vividBlendOpacity: number,
	blurDiameter: number,
): Generator<undefined, WritableBitmap> {
	// 3,8.7,2
	// repeat $! l[$>]
	//   repeat $3
	//     repeat $1 bilateral. $2,{1.5*$2} done
	//     blend[-2,-1] vividlight blend overlay
	//   done
	// endl done n 0,255
	// - amplitude: Default 2. Range 1-5. Number of applications of blur
	// - scale: Default 10. Range 10-100. sd for bilateral blur
	// - iterations: Default 1. Range 1-4. Repeat the process this many times

	const bitmapPool = new Array<WritableBitmap>();

	// const iterations = 1; // 2 is causing errors
	// for (let j = 0; j < iterations; j++) {
	let amplitudeStepInput = yield* cloneBitmapToWritable(image);
	bitwiseNot(amplitudeStepInput);

	for (let i = 0; i < amplitude; i++) {
		let amplitudeStepOutput = bitmapPool.pop();
		if (amplitudeStepOutput === undefined) {
			amplitudeStepOutput = createBlankWritableBitmap(
				amplitudeStepInput.width,
				amplitudeStepInput.height,
				undefined,
			);
		}

		// JS
		// bilateralFilter(amplitudeStepInput, amplitudeStepOutput, blurDiameter, sigmaA, sigmaB);

		// OpenCV
		const bInput = bitmapToOpenCv(openCv, amplitudeStepInput);
		const bOutput = bitmapToOpenCv(openCv, amplitudeStepOutput);
		openCv.bilateralFilter(bInput, bOutput, blurDiameter, sigmaA, sigmaB);
		amplitudeStepOutput = cloneOpenCvToBitmap(bOutput);

		bitmapPool.push(amplitudeStepInput);
		amplitudeStepInput = amplitudeStepOutput;
	}
	yield;
	const vividBlended = vividLightBlend(
		amplitudeStepInput,
		image,
		vividBlendOpacity,
	);
	yield;
	return overlayBlend(vividBlended, image);
}

// Resembling "unsharp mask" filter from PhotoShop
// Note: Detail filter should not apply to removed background areas. However it
// doesn't impact the flat bg colours
function* detailFilterOperationLegacy({
	openCv,
	config,
	intermediate: { image },
	sources: { backgroundMask },
}: BuildOperationInput): Generator<undefined, BuildOperationResult> {
	const { detailFilter, removeBackground } = config;
	if (!detailFilter) {
		throw new Error("No detail filter");
	}
	if (!isLegacyDetailFilter(detailFilter)) {
		throw new Error("Invalid legacy detail filter");
	}

	const resultImage = yield* detailFilterAlgorithm(
		openCv,
		image,
		3, // amplitude
		detailFilter.sigma, // sigma A
		detailFilter.sigma * 1.5, // sigma B
		2,
		// When this goes below 1 it takes a long time
		// Note: naming issue here, using "radius ratio" as a diameter ratio
		Math.max(1, detailFilter.radiusRatio * Math.min(image.width, image.height)),
	);

	// We don't want to apply detail filter to background.
	if (removeBackground && backgroundMask) {
		// Background mask is in scale of original source, so need some re-calc.
		const maskingProps = compileMaskingProps(config, backgroundMask, image);
		if (maskingProps) {
			const numPixelChannels = image.width * image.height * bitmapChannels;
			for (let i = 0; i < numPixelChannels; i += bitmapChannels) {
				const pixelIndex = i / bitmapChannels;
				const x = pixelIndex % image.width;
				const y = (pixelIndex - x) / image.width;
				const maskX = floatFloor(
					(x + maskingProps.croppedXOffset) / maskingProps.usedResizeScale,
				);
				const maskY = floatFloor(
					(y + maskingProps.croppedYOffset) / maskingProps.usedResizeScale,
				);

				// If total background, then use previous result. We don't mind if
				// there's some detail handling of the partial opacity mask pixels.
				if (maskingProps.foregroundRatio[maskX][maskY] === 0) {
					writeUInt32BE(resultImage.data, readUInt32BE(image.data, i), i);
					continue;
				}
			}
		}
	}

	return {
		type: "cloned",
		clonedImage: {
			noBrickColour: image.noBrickColour,
			...resultImage,
		},
	};
}

export default detailFilterOperationLegacy;
